import React, { useEffect, useMemo, useState } from 'react'
import { compose } from 'redux'
import { auth as authModule, clubs as clubsModule } from 'redux/modules'
import { withLocalize, Translate } from 'react-localize-redux'
import { useLocation, useParams, withRouter } from 'react-router-dom'
import { Grid, Box, Hidden, Typography } from '@material-ui/core'
import Link from '@material-ui/core/Link'
import { Snackbars as Snackbar } from 'components'
import { getSignupURL, setLocale } from 'utils/localstorage'
import { identify } from 'utils/segmentTracking'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import useStyles from 'routes/styleComponents'
import { Header } from 'routes/Signup/components'
import SocialLogin from 'routes/SocialLogin'
import { isEmpty } from 'lodash'
import * as Sentry from '@sentry/react'
import CfgLogo from '../../assets/cfg/logo.png'
import CfgBackground from '../../assets/cfg/bg.png'
import BmgLogo from '../../assets/bmg/logo.png'
import BmgBackground from '../../assets/bmg/bg.jpg'
import BvbNlzLogo from '../../assets/bvb-nlz/logo.png'
import BvbNlzBackground from '../../assets/bvb-nlz/bg.jpeg'
import SignInForm from './components/SignInForm'
import CfgSSO from './components/CfgSSO'
import BmgSSO from './components/BmgSSO'
import BvbNlzSSO from './components/BvbNlzSSO'
import languages from 'constants/languages'
import { DEFAULT_CLUB_THEME } from 'constants/club'

const SignInPage = ({
  auth,
  dispatchLogin,
  fetchClubs,
  theme,
  history,
  setActiveLanguage,
  isCfg,
  isBmg,
  isBvbNlz,
  createUserClub,
  fetchAuthenticatedUser,
  setAuthTokens,
}) => {
  // Get redux store theme when custom club signup otherwise its empty
  const currentTheme = isEmpty(theme) ? DEFAULT_CLUB_THEME : theme
  const isGoogleLoginEnabled =
    process.env.REACT_APP_ENABLE_GOOGLE_LOGIN === 'true'
  const isFacebookLoginEnabled =
    process.env.REACT_APP_ENABLE_FACEBOOK_LOGIN === 'true'
  const isSignupEnabled = process.env.REACT_APP_ENABLE_SIGNUP === 'true'

  const isSocialLoginEnabled = isGoogleLoginEnabled || isFacebookLoginEnabled

  // noinspection SpellCheckingInspection
  /**
   * Tweak theme on CFG/BMG/BVB NLZ SSO sign-in pages, because they require
   * organization logo and background image, and they are different
   * from the default club theme.
   */
  let tweakedTheme = currentTheme
  if (isCfg) {
    tweakedTheme = {
      ...currentTheme,
      logo: CfgLogo,
      backgroundimage: CfgBackground,
      backgroundcolor: '#FFFFFF',
      primarycolor: '#00285D',
      buttoncolor: '#FFFFFF',
    }
  } else if (isBmg) {
    tweakedTheme = {
      ...currentTheme,
      logo: BmgLogo,
      backgroundimage: BmgBackground,
      backgroundcolor: '#FFFFFF',
      primarycolor: '#00bf5f',
      buttoncolor: '#FFFFFF',
    }
  } else if (isBvbNlz) {
    tweakedTheme = {
      ...currentTheme,
      logo: BvbNlzLogo,
      backgroundimage: BvbNlzBackground,
      backgroundcolor: '#FFFFFF',
      primarycolor: '#ffe600',
      buttoncolor: '#000000',
    }
  }

  const classes = useStyles(tweakedTheme)

  const { locale } = useParams() // locale from URL (optional)
  const { search } = useLocation() // URL query parameters

  const query = useMemo(() => {
    return new URLSearchParams(search)
  }, [search])

  const [snackbar, setSnackbar] = useState(false)

  const isClubSignIn = isCfg || isBmg || isBvbNlz || !!query.get('club')

  /**
   * Get preferred language from URL :locale parameter or set it to default (EN)
   * and store value in local storage.
   */

  useEffect(() => {
    if (languages.some((lang) => lang.code === locale)) {
      setLocale(locale)
      setActiveLanguage(locale)
    } else {
      setLocale('en')
      setActiveLanguage('en')
    }
  }, [locale])

  const handleSubmit = (values) => {
    setSnackbar(false)
    Sentry.addBreadcrumb({
      category: 'ui.click',
      message: 'User clicked the Login button',
      data: {
        email: values.email,
      },
    })
    dispatchLogin(values.email, values.password, locale)
      .then((res) => {
        const { access_token: accessToken, refresh_token: refreshToken } =
          res.response

        setAuthTokens({
          access_token: accessToken,
          refresh_token: refreshToken,
        })

        return fetchAuthenticatedUser()
      })
      .then((res) => {
        const userData = res.response.data

        identify(userData.uuid)
        setLocale(userData.language)
        setActiveLanguage(userData.language)

        return fetchClubs()
      })
      // eslint-disable-next-line consistent-return
      .then((data) => {
        const userClubs = data.response.data

        if (userClubs.length > 0) {
          history.push('/')
        } else {
          return createUserClub().then(() => {
            history.push(`${getSignupURL()}/step2`)
          })
        }
      })
      .catch((error) => {
        setSnackbar({
          message: <Translate id="login.wrong" />,
          error: true,
        })
        Sentry.captureException(error)
      })
  }

  const handleLanguage = (event) => {
    setLocale(event.target.value)
    setActiveLanguage(event.target.value)
  }

  const isOrgSso = isCfg || isBmg || isBvbNlz

  const heading = isOrgSso ? (
    'Sign in to your account'
  ) : (
    <Translate id="login.heading" />
  )

  const subHeading = isOrgSso ? '' : <Translate id="login.sub-heading" />

  return (
    <>
      <Grid container spacing={0} className={classes.root}>
        <Hidden only={['md', 'lg', 'xl']}>
          <Box className={classes.SignupTopText}>
            For full experience use Coachbetter
            <br /> on your desktop computer or tablet
          </Box>
        </Hidden>
        <Grid md={6} item container spacing={0}>
          <Grid item xs={12} className={classes.centeredGridContent}>
            <Box className={classes.loginBox}>
              <Header
                theme={tweakedTheme}
                heading={heading}
                subHeading={subHeading}
              />
              {isCfg ? (
                <CfgSSO theme={tweakedTheme} />
              ) : isBmg ? (
                <BmgSSO theme={tweakedTheme} />
              ) : isBvbNlz ? (
                <BvbNlzSSO theme={tweakedTheme} />
              ) : (
                <SignInForm
                  isShowLanguageDropdown={!isClubSignIn}
                  onLanguageChange={handleLanguage}
                  inProgress={auth.isLoading}
                  theme={tweakedTheme}
                  onSubmit={handleSubmit}
                />
              )}
              {!isClubSignIn && isSignupEnabled && (
                <>
                  {isSocialLoginEnabled && (
                    <Box mb={1}>
                      <SocialLogin
                        history={history}
                        theme={tweakedTheme}
                        page="login"
                        googleLink={<Translate id="login.signin-with-google" />}
                        facebookLink={
                          <Translate id="login.signin-with-facebook" />
                        }
                        isFacebookLoginEnabled={isFacebookLoginEnabled}
                        isGoogleLoginEnabled={isGoogleLoginEnabled}
                      />
                    </Box>
                  )}
                  <Box mt={0.8} align="center">
                    <Typography
                      variant="body1"
                      component="p"
                      className={classes.typography}
                    >
                      <Translate id="login.dont-have-acount" /> &nbsp;
                      <Link
                        href={getSignupURL()}
                        className={classes.link}
                        underline="always"
                      >
                        <Translate id="login.signup" />
                      </Link>
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
        <Hidden only={['xs', 'sm']}>
          <Grid md={6} item>
            <Box className={classes.background} />
          </Grid>
        </Hidden>
      </Grid>
      {snackbar && <Snackbar {...snackbar} />}
    </>
  )
}

SignInPage.defaultProps = {
  isCfg: false,
  isBmg: false,
  isBvbNlz: false,
  theme: {},
}

SignInPage.propTypes = {
  auth: PropTypes.shape().isRequired,
  dispatchLogin: PropTypes.func.isRequired,
  fetchClubs: PropTypes.func.isRequired,
  createUserClub: PropTypes.func.isRequired,
  theme: PropTypes.oneOfType([PropTypes.shape(), PropTypes.oneOf([null])]),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  setActiveLanguage: PropTypes.func.isRequired,
  isCfg: PropTypes.bool,
  isBmg: PropTypes.bool,
  isBvbNlz: PropTypes.bool,
  fetchAuthenticatedUser: PropTypes.func.isRequired,
  setAuthTokens: PropTypes.func.isRequired,
}

export default compose(
  withRouter,
  withLocalize,
  connect(
    ({ auth, clubs }) => ({
      auth,
      theme: clubs.current ? clubs.current.theme : {},
    }),
    {
      dispatchLogin: authModule.login,
      fetchClubs: clubsModule.fetchClubs,
      createUserClub: clubsModule.createUserClub,
      fetchAuthenticatedUser: authModule.fetchUser,
      setAuthTokens: authModule.setAuthTokens,
    }
  )
)(SignInPage)
